import * as React from 'react';
import { toast } from 'react-toastify';
import Invoices from './Invoices';
import type { GenericList } from './models/JobSharedInterfaces';
import { useState } from 'react';
import type { InvoiceDetails, InvoiceItems } from './models/InvoiceInterfaces';
import { useNavigate } from 'react-router-dom';
import { GetQuery, PostQuery } from './Shared';


const InvoiceSearchData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [gridLoading, setGridLoading] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetails[]>([]);
    const [invoiceTotalFormatted, setInvoiceTotalFormatted] = useState("$0.00");
    const [canCreate, setCanCreate] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canEditHirePeriod, setCanEditHirePeriod] = useState(false);
    const [canView, setCanView] = useState(true);
    const [canSend, setCanSend] = useState(false);
    const [canUpdatePaid, setCanUpdatePaid] = useState(false);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageSize, setPageSize] = useState(30);
    const [customerId, setCustomerId] = useState(-1);
    const [siteAddress, setSiteAddress] = useState("");
    const [jobId, setJobId] = useState(0);
    const [invoiceNumber, setInvoiceNumber] = useState(0);
    const [reckonInvoiceNo, setReckonInvoiceNo] = useState(0);
    const [statusId, setStatusId] = useState(-1);
    const [customers, setCustomers] = useState<GenericList[]>([]);
    const [statusList, setStatusList] = useState<GenericList[]>([]);
    const [searched, setSearched] = useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const apiUrl = "CheckInvoicingPermission";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            if (result.data.canView == true) {
                setCanView(result.data.canView);
                setCanCreate(result.data.canCreate);
                setCanEdit(result.data.canEdit);
                setCanEditHirePeriod(result.data.canEditHirePeriod);
                setCanSend(result.data.canSend);
                setCanUpdatePaid(result.data.canUpdatePaid);
                getInvoiceSearchData();
            } else {
                //redirect to 403 permission denied
                navigate("/accessdenied")
            }
        };
    }

    const getInvoiceSearchData = async () => {
        //get dropdowns
        const apiUrl = "GetInvoiceSearchDropdowns";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setCustomers(result.data.customers);
            setStatusList(result.data.statusOptions);
            setLoading(false);
        };
    }

    const getInvoices = async (page: number, getTotalRecords: boolean) => {
        var totalInvRecords = getTotalRecords ? 0 : totalRecords;

        const apiUrl = "SearchInvoices?page=" + page + "&totalRecords=" + totalInvRecords + "&getTotalRecords=" + getTotalRecords + "&customerId=" + customerId + "&siteAddress=" + siteAddress + "&jobId=" + jobId + "&invoiceNumber=" + invoiceNumber + "&reckonInvoiceNo=" + reckonInvoiceNo + "&statusId=" + statusId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            var invoices = result.data.invoices;
            for (var i = 0; i < invoices.length; i++) {
                var invoice = invoices[i];

                for (var y = 0; y < invoice.items.length; y++) {
                    invoice.items[y].itemIndex = y;
                    invoice.items[y].amount = invoice.items[y].amount.toFixed(2);
                }
                //add blank row if canEdit invoice and user can edit
                if (invoice.canEdit && canEdit) {
                    var newRow: InvoiceItems = {
                        itemIndex: invoice.items.length,
                        id: 0,
                        jobInvoiceId: invoice.id,
                        branchIdent: "",
                        item: "",
                        itemId: -1,
                        itemTypeId: -1,
                        invItemType: "",
                        invItemTypeId: -1,
                        quantityType: "",
                        purchaseOrderNumber: "",
                        additionalHireRate: "0.00",
                        rate: "0.00",
                        rateFormatted: "$0.00",
                        gst: "0.00",
                        gstFormatted: "$0.00",
                        chargeGst: true,
                        amount: "",
                        amountFormatted: "$0.00",
                        description: "",
                        status: 1,
                        invoiceStartDate: "",
                        invoiceEndDate: "",
                        invoiceStartDateFormatted: "",
                        invoiceEndDateFormatted: "",
                        hirePeriodDays: "",
                        hirePeriod: "",
                        dayRate: "",
                        weekMonthRate: "",
                        isDirty: false
                    }
                    invoice.items.push(newRow);
                }
            }

            setSearched(true);
            setGridLoading(false);
            setInvoiceDetails(invoices);
            setInvoiceTotalFormatted(result.data.invoiceTotalFormatted);
            setPage(page);
            setTotalRecords(result.data.totalRecords);
            setPageSize(result.data.pageSize);
            setLoading(false);
        };
 
    }

    const reset = () => {
        setSearched(false);
        setGridLoading(false);
        setCustomerId(-1);
        setSiteAddress("");
        setJobId(0);
        setInvoiceNumber(0);
        setReckonInvoiceNo(0);
        setStatusId(-1);
        setInvoiceDetails([]);
    }

    const search = (e: any) => {
        e.preventDefault();
        if (customerId <= 0 && !siteAddress && jobId <= 1 && !invoiceNumber && !reckonInvoiceNo && statusId <= 0) {
            toast.error("Please enter at least one search criteria!");
        } else {
            getInvoices(1, true);
            setPage(1);
            setSearched(false);
            setGridLoading(true);
        }
    }

    const submit = (updatedInvoices: InvoiceDetails[]) => {
        if (updatedInvoices.length > 0) {
            saveInvoices(updatedInvoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    const saveInvoices = async (invoices: InvoiceDetails[]) => {
        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                customerId: invoices[inv].customerId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems
        }

        //save
        var jsonItem = JSON.stringify(invDetails);

        const apiUrl = "SaveInvoiceItems";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            getInvoices(page, false);
            toast.success("Invoices Saved");
        };
    }

    const saveHirePeriod = (updatedInvoices: InvoiceDetails[]) => {
        if (updatedInvoices.length > 0) {
            saveInvoiceHirePeriod(updatedInvoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    const saveInvoiceHirePeriod = async (invoices: InvoiceDetails[]) => {
        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems
        }

        //save
        var jsonItem = JSON.stringify(invDetails);

        const apiUrl = "SaveInvoiceItemHirePeriod";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            getInvoices(page, false);
            toast.success("Invoices Saved");
        };
    }

    const updateInvoiceDetails = (updatedInvoices: InvoiceDetails[]) => {
        setInvoiceDetails(updatedInvoices);
    }

    const pageChanged = (page: number) => {
        setPage(page);
        setGridLoading(true);
        getInvoices(page, false);
    }

    const addNew = () => {
        navigate("/invoiceadd");
    }

   
    var invoiceList = <Invoices invoiceDetails={invoiceDetails} invoiceTotalFormatted={invoiceTotalFormatted} showCustType={false} showJobInfo={true} showQuoteNo={false} showHirePeriod={true} showStatus={true} showStartDate={false} showPaidDate={true} showPaid={true} showSend={true} showRemove={false} showSentTo={false} showReckonInvNo={true} showError={false} showSent={false} canEdit={canEdit} canEditHirePeriod={canEditHirePeriod} canSend={canSend} canUpdatePaid={canUpdatePaid} page={page} pageSize={pageSize} totalRecords={totalRecords} pageChanged={pageChanged} beforeValid={updateInvoiceDetails} afterValid={submit} saveHirePeriod={saveHirePeriod} />

    const renderDetails = (
        <div>
            <div className="marginTop20">
                <div className={canView ? "hidden" : ""}>
                    <h2 className="error">You do not have permission to view this page!</h2>
                </div>
                <div className={canView ? "" : "hidden"}>
                    <div className="input-group-parent">
                        <label className="input-group" htmlFor="customer">
                            <span className="label">Customer</span>
                            <select className="select" id="customer" name="customerId" value={customerId} onChange={(e) => setCustomerId(Number(e.target.value))}>
                                <option defaultValue="-1"></option>
                                {customers.map(cust =>
                                    <option key={cust.id} value={cust.id}>{cust.name}</option>
                                )};
                            </select>
                        </label>
                        <label className="input-group" htmlFor="siteAddress">
                            <span className="label label--setsizesm">Site Address</span>
                            <input className="input" type="text" id="siteAddress" name="siteAddress" value={siteAddress} onChange={(e) => setSiteAddress(e.target.value)}></input>
                        </label>
                        <label className="input-group" htmlFor="jobId">
                            <span className="label label--setsizesm">Job Id</span>
                            <input className="input" type="text" id="jobId" name="jobId" value={jobId} onChange={(e) => setJobId(Number(e.target.value))}></input>
                        </label>
                        <label className="input-group" htmlFor="status">
                            <span className="label">Status</span>
                            <select className="select" id="status" name="statusId" value={statusId} onChange={(e) => setStatusId(Number(e.target.value))}>
                                <option defaultValue="-1"></option>
                                {statusList.map(status =>
                                    <option key={status.id} value={status.id}>{status.name}</option>
                                )};
                            </select>
                        </label>
                    </div>

                    <div className="input-group-parent">
                        <label className="input-group" htmlFor="invoiceNumber">
                            <span className="label">Dash Invoice No</span>
                            <input type='text' id="invoiceNumber" name="invoiceNumber" className='input' value={invoiceNumber} onChange={(e) => setInvoiceNumber(Number(e.target.value))}></input>
                        </label>
                        <label className="input-group" htmlFor="reckonInvoiceNo">
                            <span className="label">Reckon Invoice No</span>
                            <input type='text' id="reckonInvoiceNo" name="reckonInvoiceNo" className='input' value={reckonInvoiceNo} onChange={(e) => setReckonInvoiceNo(Number(e.target.value))}></input>
                        </label>
                    </div>

                    <div className="defaultbutton__container--right">
                        <button className="defaultbutton marginRight10" type='button' onClick={reset}>Reset</button>
                        <button className="defaultbutton" type='button' onClick={search}>Search</button>
                    </div>
                    <div>
                        <hr></hr>
                    </div>
                    <div className={gridLoading ? "" : "hidden"}>
                        <p><em>Loading...</em></p>
                    </div>
                    <div className={invoiceDetails.length > 0 && !gridLoading ? "" : "hidden"}>
                        {invoiceList}
                    </div>
                    <div className={invoiceDetails.length == 0 && searched && !gridLoading ? "" : "hidden"}>
                        No Records Found - Please try modifying your search criteria
                    </div>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;


    return (
        <div>
            <div className="makeitflexspacebetween">
                <h1>Invoice Search</h1>
                <button className="defaultbutton defaultbutton__small marginTop10" type='button' onClick={addNew} disabled={!canCreate}>Add New</button>
            </div>
            {contents}
        </div>
    );

};

export default InvoiceSearchData;
